import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { headers } from './Config/apiConfig';
import { ToastContext } from '../../context/ToastContext';
import * as listCache from '../../services/listCache';

/**
 * Generic "convert / advance pipeline stage" button.
 * POSTs `body` to `url`, then navigates to `buildNavigateTo(responseData)` on success.
 */
const ConvertActionButton = ({ label, url, body = {}, buildNavigateTo, onDone, className = 'btn btn-primary', disabled, disabledTitle }) => {
  const [loading, setLoading] = useState(false);
  const toast = useContext(ToastContext);
  const navigate = useNavigate();

  const handleClick = async () => {
    const resolvedBody = typeof body === 'function' ? body() : body;
    if (resolvedBody === null) return; // body() returned null -> user cancelled (e.g. a prompt)
    setLoading(true);
    try {
      const res = await axios.post(url, resolvedBody, { headers });
      const data = res.data?.data ?? res.data;
      // Pipeline actions typically touch more than one entity's list (e.g.
      // converting a Lead also creates an Opportunity) — a full clear is
      // simpler and safer here than guessing which specific lists changed.
      listCache.invalidateAll();
      toast?.addToast({ type: 'success', message: res.data?.message || `${label} succeeded.` });
      if (onDone) onDone(data);
      if (buildNavigateTo) navigate(buildNavigateTo(data));
    } catch (err) {
      const errData = err?.response?.data;
      const msg = errData?.message || (errData?.data && JSON.stringify(errData.data)) || `Failed: ${label}`;
      toast?.addToast({ type: 'error', message: msg, duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled || loading}
      title={disabled ? disabledTitle : undefined}
    >
      {loading ? 'Working…' : label}
    </button>
  );
};

export default ConvertActionButton;
